import { useState, type FormEvent } from 'react'
import {
  EQUIPMENT_CATEGORIES,
  SELECTABLE_WORKING_STATES,
  type EquipmentItemInput,
  type WorkingState,
} from '@mads/shared'
import { MadsCodeField } from './MadsCodeField'
import { ComboField } from './ComboField'
import { ErrorNote } from './Feedback'
import { Select } from './Select'
import { isElectrical, toInput, type EquipmentFormValues } from './equipmentFormValues'
import { useAutocomplete, useFixtureModels, useLocations } from '@/lib/queries'

interface EquipmentFormProps {
  initial: EquipmentFormValues
  submitLabel: string
  busy?: boolean
  error?: unknown
  onSubmit: (input: EquipmentItemInput) => void
  onCancel?: () => void
}

/**
 * The capture form (spec §3.2 Add equipment): a single-minute form with
 * autocomplete on category and brand so repeat items are fast to log. The
 * category segmented control decides which fields are shown — PAT for
 * electrical assets, optics for lanterns.
 */
export function EquipmentForm({
  initial,
  submitLabel,
  busy = false,
  error,
  onSubmit,
  onCancel,
}: EquipmentFormProps) {
  const [values, setValues] = useState<EquipmentFormValues>(initial)
  const [validationError, setValidationError] = useState<string | null>(null)

  const locations = useLocations()
  const autocomplete = useAutocomplete()
  const fixtureModels = useFixtureModels()

  const linkedModel =
    (fixtureModels.data ?? []).find((model) => String(model.id) === values.fixtureModelId) ?? null

  function set<K extends keyof EquipmentFormValues>(key: K, value: EquipmentFormValues[K]) {
    setValues((previous) => ({ ...previous, [key]: value }))
  }

  /**
   * Picking a model fills in the name and the brand from it, which is what
   * makes logging the ninth identical Fresnel a two-field job: the model
   * already knows what the thing is called and who made it.
   *
   * **Only into an empty box.** Anything already typed is somebody's, and a
   * picker that overwrote it would be a way to lose work by changing your mind
   * about which model this is — the same reasoning that keeps a copy-down from
   * overwriting an overridden lamp (issue #59). On the edit form both fields
   * are always filled, so this never fires there.
   */
  function linkFixtureModel(fixtureModelId: string) {
    const model = (fixtureModels.data ?? []).find((m) => String(m.id) === fixtureModelId) ?? null

    setValues((previous) => ({
      ...previous,
      fixtureModelId,
      name: previous.name.trim() === '' ? (model?.name ?? '') : previous.name,
      brand: previous.brand.trim() === '' ? (model?.brand ?? '') : previous.brand,
    }))
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (values.name.trim() === '') {
      setValidationError('Give the item a name.')
      return
    }

    if (values.quantity !== '' && Number(values.quantity) < 0) {
      setValidationError('Quantity cannot be negative.')
      return
    }

    setValidationError(null)
    onSubmit(toInput(values))
  }

  const lists = autocomplete.data

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 520 }}>
      {validationError !== null && (
        <div className="error-note" role="alert">
          {validationError}
        </div>
      )}
      <ErrorNote error={error} />

      <div className="field">
        <label id="category-label">Category</label>
        <div className="seg" role="radiogroup" aria-labelledby="category-label">
          {EQUIPMENT_CATEGORIES.map((option) => (
            <label className="seg-opt" key={option}>
              <input
                type="radio"
                name="category"
                value={option}
                checked={values.category === option}
                onChange={() => set('category', option)}
              />
              {option.replace(' Fixture', '').replace(' Equipment', '')}
            </label>
          ))}
        </div>
      </div>

      <div className="field">
        <label htmlFor="name">Name</label>
        <input
          id="name"
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
          className="input"
          value={values.name}
          onChange={(event) => set('name', event.target.value)}
          required
        />
      </div>

      <ComboField
        id="brand"
        label="Brand"
        value={values.brand}
        options={lists?.brands ?? []}
        onChange={(value) => set('brand', value)}
      />

      <div className="field">
        <label htmlFor="quantity">Quantity</label>
        <input
          id="quantity"
          className="input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={values.quantity}
          onChange={(event) => set('quantity', event.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="locationId">Location</label>
        <Select
          inputId="locationId"
          value={values.locationId}
          onChange={(value) => set('locationId', value)}
          options={[
            { value: '', label: 'No location recorded' },
            ...(locations.data ?? []).map((location) => ({
              value: String(location.id),
              label: location.name,
            })),
          ]}
        />
      </div>

      <div className="field">
        <label htmlFor="notes">Notes</label>
        <textarea
          id="notes"
          className="input"
          rows={3}
          value={values.notes}
          onChange={(event) => set('notes', event.target.value)}
        />
      </div>

      {isElectrical(values.category) && (
        <>
          <MadsCodeField
            value={values.madsCode}
            onChange={(value) => set('madsCode', value)}
            disabled={busy}
          />

          <div className="field">
            <label htmlFor="patTestCode">PAT test code</label>
            <input
              id="patTestCode"
              className="input"
              value={values.patTestCode}
              onChange={(event) => set('patTestCode', event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="patTestDate">PAT test date</label>
            <input
              id="patTestDate"
              className="input"
              type="date"
              value={values.patTestDate}
              onChange={(event) => set('patTestDate', event.target.value)}
            />
          </div>

          {/*
            * Four states since issue #59, so a checkbox no longer says it:
            * "not ticked" cannot tell a fault apart from kit nobody has
            * checked. `Not applicable` is absent on purpose — it is what
            * reclassifying to cabling sets, not something anybody chooses.
            */}
          <div className="field">
            <label htmlFor="workingState">Working state</label>
            <Select
              inputId="workingState"
              value={values.workingState}
              onChange={(value) => set('workingState', value as WorkingState)}
              options={SELECTABLE_WORKING_STATES.map((option) => ({
                value: option,
                label: option,
              }))}
            />
          </div>
        </>
      )}

      {values.category === 'Lighting Fixture' && (
        <>
          <div className="field">
            <label htmlFor="fixtureModelId">Fixture model</label>
            <Select
              inputId="fixtureModelId"
              value={values.fixtureModelId}
              onChange={linkFixtureModel}
              options={[
                { value: '', label: 'Not linked — describe this item' },
                ...(fixtureModels.data ?? []).map((model) => ({
                  value: String(model.id),
                  // The display name, like everywhere else a model is named
                  // (issue #59) — a picker offering a different string from the
                  // page it links through to is two names for one thing.
                  label: model.displayName,
                })),
              ]}
            />
            <p className="text-muted" style={{ margin: 'var(--space-1) 0 0', fontSize: 13 }}>
              {linkedModel === null
                ? 'Link identical lanterns to a model so the optics are recorded once.'
                : 'The optics below come from this model — edit them on the model to change every lantern linked to it. The lamp and the watts can be overridden here for this unit alone.'}
            </p>
          </div>

          {linkedModel !== null && (
            <div className="detail-grid" style={{ marginBottom: 'var(--space-4)' }}>
              {(
                [
                  ['Type', linkedModel.lightType],
                  ['Gel frame', linkedModel.gelFrame],
                  ['Gobo frame', linkedModel.goboFrame],
                  ['Barn door', linkedModel.barnDoor],
                ] as Array<[string, string | null]>
              ).map(([label, value]) => (
                <div key={label}>
                  <div className="detail-label">{label}</div>
                  <div className="detail-value">{value ?? '—'}</div>
                </div>
              ))}
            </div>
          )}

          {linkedModel === null && (
            <ComboField
              id="lightType"
              label="Type"
              value={values.lightType}
              options={lists?.lightTypes ?? []}
              onChange={(value) => set('lightType', value)}
            />
          )}

          {/*
            * The lamp and the watts are askable whether or not a model is
            * linked (issue #59): a lantern re-lamped with something other than
            * what the model says records it here. Left empty, the box shows
            * the model's value as a placeholder and the record inherits it —
            * which is why it is not seeded with that value. An input showing
            * an inherited value with no way to tell it apart from an override
            * is how somebody edits a value they thought was shared.
            */}
          <ComboField
            id="lamp"
            label="Lamp"
            value={values.lamp}
            options={lists?.lamps ?? []}
            onChange={(value) => set('lamp', value)}
            placeholder={linkedModel?.lamp ?? undefined}
            hint={
              linkedModel === null
                ? undefined
                : values.lamp.trim() === ''
                  ? `Inherited from ${linkedModel.displayName}.`
                  : 'Overrides the model. Clear the box to go back to inheriting it.'
            }
          />

          <div className="field">
            <label htmlFor="watts">Watts</label>
            <input
              id="watts"
              className="input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={
                linkedModel === null || linkedModel.watts === null
                  ? undefined
                  : String(linkedModel.watts)
              }
              value={values.watts}
              onChange={(event) => set('watts', event.target.value)}
            />
            {linkedModel !== null && (
              <p className="text-muted" style={{ margin: 'var(--space-1) 0 0', fontSize: 13 }}>
                {values.watts.trim() === ''
                  ? `Inherited from ${linkedModel.displayName}.`
                  : 'Overrides the model. Clear the box to go back to inheriting it.'}
              </p>
            )}
          </div>

          {linkedModel === null && (
            <>
              <ComboField
                id="gelFrame"
                label="Gel frame"
                value={values.gelFrame}
                options={lists?.gelFrames ?? []}
                onChange={(value) => set('gelFrame', value)}
              />

              <ComboField
                id="goboFrame"
                label="Gobo frame"
                value={values.goboFrame}
                options={lists?.goboFrames ?? []}
                onChange={(value) => set('goboFrame', value)}
              />

              <ComboField
                id="barnDoor"
                label="Barn door"
                value={values.barnDoor}
                options={lists?.barnDoors ?? []}
                onChange={(value) => set('barnDoor', value)}
              />
            </>
          )}
        </>
      )}

      <div className="action-row" style={{ marginTop: 'var(--space-4)' }}>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onCancel !== undefined && (
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}
